import { Button, Col, Input, Row, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import MapContainer from './MapContainer';
import { useMapTaxiBusinessData } from '../hooks/useMapTaxiBusinessData';
import { ReloadOutlined } from '@ant-design/icons';
import { useGetMapTaxiBusinessUsersQuery } from '../../../GraphQL/hooks/useGetMapTaxiBusinessUsersQuery';
import { AccountStatusEnums, MapTaxiBusinessUsersQueryFilter } from '../../../shared/types';

const Option = Select.Option;
const MapContent = () => {
    const [search, setSearch] = useState('');
    const [userType, setUserType] = useState('All');
    const [active, setActive] = useState('All');
    const [online, setOnline] = useState('All');
    const [updatedWithin, setUpdatedWithin] = useState('All');
    const [enableClustering, setEnableClustering] = useState(true);
    const [liveTracking, setLiveTracking] = useState(false);
    const [refreshRate, setRefreshRate] = useState(5000);

    const request = useMemo(
        () =>
            ({
                search: search ?? '',

                userType: userType !== 'All' ? userType : 'All',

                active: active !== 'All' ? active : 'All',

                online: online !== 'All' ? online : 'All',

                updatedWithin: updatedWithin !== 'All' ? updatedWithin : 'All',
            }) as MapTaxiBusinessUsersQueryFilter,

        [search, userType, active, online, updatedWithin]
    );

    const { data: response, refetch, loading: isLoading } = useGetMapTaxiBusinessUsersQuery(request);

    const {
        mapData: { riders, drivers, isClusteringMode },
        setMapData: setMapData,
    } = useMapTaxiBusinessData();

    useEffect(() => {
        if (!isLoading) {
            setMapData({
                isClusteringMode: enableClustering,
                riders: response.riders,
                drivers: response.drivers,
            });
        }
    }, [response, enableClustering]);

    useEffect(() => {
        if (!liveTracking) return;

        const timer = setInterval(refetch, refreshRate);

        return () => clearInterval(timer);
    }, [liveTracking, refreshRate, refetch]);

    const handleRefresh = () => {
        refetch();
    };

    return (
        <>
            <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                <Col span={5}>
                    <Input placeholder="Search by name" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>

                <Col span={3}>
                    <Select style={{ width: '100%' }} value={userType} onChange={setUserType}>
                        <Option value="All">All</Option>
                        <Option value="rider">Riders</Option>
                        <Option value="driver">Drivers</Option>
                    </Select>
                </Col>

                <Col span={3}>
                    <Select style={{ width: '100%' }} value={active} onChange={setActive}>
                        <Option value="All">All</Option>
                        <Option value={AccountStatusEnums.Active}>Active</Option>
                        <Option value={AccountStatusEnums.InActive}>InActive</Option>
                    </Select>
                </Col>

                <Col span={3}>
                    <Select style={{ width: '100%' }} value={online} onChange={setOnline}>
                        <Option value="All">All</Option>
                        <Option value="Online">Online</Option>
                        <Option value="Offline">Offline</Option>
                    </Select>
                </Col>

                <Col span={3}>
                    <Select style={{ width: '100%' }} value={updatedWithin} onChange={setUpdatedWithin}>
                        <Option value="All">All Time</Option>

                        <Option value="5m">Last 5 min</Option>

                        <Option value="15m">Last 15 min</Option>

                        <Option value="30m">Last 30 min</Option>

                        <Option value="1h">Last 1 hour</Option>

                        <Option value="2h">Last 2 hours</Option>

                        <Option value="3h">Last 3 hours</Option>

                        <Option value="4h">Last 4 hours</Option>

                        <Option value="yesterday">Yesterday</Option>
                    </Select>
                </Col>

                <Col >
                    <Select
                        value={refreshRate}
                        onChange={setRefreshRate}
                    >
                        <Option value={5000}>5 sec</Option>
                        <Option value={10000}>10 sec</Option>
                        <Option value={30000}>30 sec</Option>
                        <Option value={60000}>1 minute</Option>
                        <Option value={180000}>3 minutes</Option>
                        <Option value={300000}>5 minutes</Option>
                        <Option value={600000}>10 minutes</Option>
                    </Select>
                </Col>

                <Col>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
                        Refresh
                    </Button>
                </Col>
            </Row>

            <Row style={{ marginTop: 16, marginBottom: 16 }}>
                <Col span={24}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Left side */}
                        <div style={{ display: 'flex', gap: 20 }}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isClusteringMode}
                                    onChange={(e) => setEnableClustering(e.target.checked)}
                                />
                                {' '}Enable clustering
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={liveTracking}
                                    onChange={(e) => setLiveTracking(e.target.checked)}
                                />
                                {' '}Live Tracking
                            </label>
                        </div>

                        {/* Center counts */}
                        <div
                            style={{
                                display: 'flex',
                                gap: 40,
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontWeight: 500,
                                }}
                            >
                                <img src="/assets/pin_passenger.png" alt="riders" style={{ width: 28, height: 28 }} />
                                Riders: {riders?.length ?? 0}
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontWeight: 500,
                                }}
                            >
                                <img src="/assets/pin_taxi.png" alt="drivers" style={{ width: 32, height: 32 }} />
                                Drivers: {drivers?.length ?? 0}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            {!isLoading ? <MapContainer /> : <div style={{ textAlign: 'center' }}>Loading...</div>}
        </>
    );
};

export default MapContent;
